import {
  AUTH_PAGES,
  type Me,
  type ModuleDef,
  type NavPlacement,
  Roles,
  routes,
  visibleModules,
} from '@pmai/contracts';
import { AppShell, type NavItem, Spinner } from '@pmai/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useApiMutation } from '../shared/api/hooks';
import { useApplySession, useSession } from '../shared/auth/session';
import { useEnumLabel, useErrorText } from '../shared/i18n';
import { NotFoundPage } from '../shared/system-pages';

/** Root layout of the signed-in app: session guard (FR-AUTH-001) + the one AppShell. */
export function AppLayout() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const session = useSession();
  const location = useLocation();

  if (session.isPending) return <Spinner label={t('common.loading')} />;
  if (session.isError) return <NotFoundPage title={errorText(session.error)} description="" />;
  if (!session.data) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${AUTH_PAGES.login}?next=${next}`} replace />;
  }
  return (
    <Shell me={session.data}>
      <Outlet />
    </Shell>
  );
}

function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const { t } = useTranslation();
  const roleLabel = useEnumLabel();
  const navigate = useNavigate();
  const applySession = useApplySession();
  const item = (m: ModuleDef): NavItem => ({
    key: m.key,
    label: t(m.labelKey),
    to: m.path ?? '/',
    icon: m.icon,
  });
  const menu = (placement: NavPlacement) => visibleModules(placement, me.permissions).map(item);

  const switchWorkspace = useApiMutation(routes.auth.switchWorkspace, {
    onSuccess: (next) => {
      applySession(next);
      void navigate('/');
    },
  });
  // A full page load after logout: nothing of the session survives in memory (cache, CSRF token).
  const logout = useApiMutation(routes.auth.logout, {
    onSettled: () => window.location.replace(AUTH_PAGES.login),
  });

  return (
    <AppShell
      brand={t('app.name')}
      mainNavLabel={t('app.mainNav')}
      primary={menu('primary')}
      overflow={menu('overflow')}
      actions={menu('action')}
      workspace={{
        label: t('app.workspace'),
        currentId: me.workspace?.workspaceId ?? null,
        currentLabel: me.workspace?.workspaceName ?? t('app.noWorkspaceShort'),
        options: me.memberships.map((m) => ({
          id: m.workspaceId,
          label: m.workspaceName,
          hint: roleLabel(Roles.byCode(m.role)),
        })),
        onSelect: (workspaceId) => {
          if (workspaceId !== me.workspace?.workspaceId) switchWorkspace.mutate({ body: { workspaceId } });
        },
      }}
      user={{
        label: t('app.userMenu'),
        name: me.user.displayName,
        email: me.user.email,
        settings: menu('settings'),
        settingsLabel: t('app.settings'),
        logoutLabel: t('app.logout'),
        onLogout: () => logout.mutate({}),
      }}
    >
      {children}
    </AppShell>
  );
}
